import { GROUP_TYPE } from '@/constants/area-constants';
import { Field, useFormikContext } from 'formik';
import { FC, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import useFocusOnRouteChange from '../../hooks/useFocusOnRouteChange';
import { ListQuery } from '../../models/ListQuery';
import { getAccessAreaNamesWithPagination } from '../../services/accessAreaService';
import { getAccessTimePatternNamesWithPagination } from '../../services/accessTimePatternService';
import { IAppState } from '../../store/types';
import { appendFilter } from '../../utilities/filters';
import { validateItemName } from '../../utilities/Validation';
import EntityHierarchy from '../entityHierarchy/entityHierarchy';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';
import Togglebox from '../togglebox/togglebox';

interface AccessGroupCardProps {
    agType: GROUP_TYPE;
}

export const AccessGroupCard: FC<AccessGroupCardProps> = ({ agType }) => {
    const { errors, values, setFieldValue } = useFormikContext<any>();
    const { translate } = useLocale();
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const inputRef = useRef<HTMLInputElement>(null);

    //When global site selector is change make sure you clear field that depend on it
    useEffect(() => {
        if (!!!values.id) {
            setFieldValue("accessTimePatternId", "");
            setFieldValue("accessAreaId", "");
        }
    }, [siteId])
    
    useFocusOnRouteChange(inputRef);

    return <><div className="w-full p-2 inline-flex justify-start items-center bg-white">
        {values.id
            ? <div className="ml-8 mr-5  card-details">
                <EntityHierarchy title={values.name} tree={{ label: values.site, node: { label: values.name } }} />
            </div>
            : null}
        <div className="flex flex-col py-4 px-8">
            <div className="mb-2">
                <div className="flex flex-row justify-between" id='nameLabel'>
                    <Label
                        htmlFor="name"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.name)}:</Label>
                    {errors.name ? <span className='text-red-600 text-xs'>{errors.name}</span> : null}
                </div>

                <div className="flex flex-row justify-between">
                    <Field
                        type="text"
                        name="name"
                        id="nameInput"
                        innerRef={inputRef}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                        validate={(value) => validateItemName(value, translate)} />
                </div>
            </div>
            <div className="flex flex-row justify-between" id='accessAreaLabelId'>
                <Label
                    htmlFor="accessArea"
                    className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.accessArea)}:</Label>
                {errors.accessAreaId ? <span className='text-red-600 text-xs'>{errors.accessAreaId}</span> : null}
            </div>
            <div id='accessAreaDropDownField'>
                <PaginatedSearchableSelectField
                    name={translate.byKey(TranslationKeys.accessArea)}
                    accessor='accessAreaId'
                    key={"accessAreaSelectField"}
                    dataMethod={(listParameters?: ListQuery) => getAccessAreaNamesWithPagination(agType, { ...listParameters, filters: appendFilter(values.siteId ? values.siteId : siteId, 'Site', 'site.id', listParameters?.filters ?? []) })}
                    searchAccessor={'areaName'}
                    required={true}
                    onSelect={(_,item) => {   
                       setFieldValue('area', item?.label)
                       setFieldValue('accessAreaId', item?.value)                       
                    }}
                    dependent={{ accessor: "siteId", key: "site.id", name: "Site", value: values.id ? values.siteId : siteId, childAcceccor: "accessAreaId" }}
                    selectedItemLabel={values.area} />
            </div>
        </div>
        <div className="flex flex-col py-4 px-8">
            <div className="flex flex-row justify-between" id='accessTimePatternId'>
                <Label
                    htmlFor="accessTimePattern"
                    className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.accessTimePattern)}:</Label>
                {errors.accessTimePatternId ? <span className='text-red-600 text-xs'>{errors.accessTimePatternId}</span> : null}
            </div>
            <div className="mb-2" id='accessTimePatternDropDownField'>
                <PaginatedSearchableSelectField
                    name={translate.byKey(TranslationKeys.accessTimePattern)}
                    key={"accessTimePatternSelectField"}
                    accessor='accessTimePatternId'
                    dataMethod={(listParameters?: ListQuery) => getAccessTimePatternNamesWithPagination({ ...listParameters, filters: appendFilter(values.id ? values.siteId : siteId, 'Site', 'site.id', listParameters?.filters ?? []) })}
                    searchAccessor={'atpName'}
                    required={true}
                    onSelect={(event, item) => {                                                
                        setFieldValue('accessTimePatterns', item?.label)
                        setFieldValue('accessTimePatternId', item?.value)
                    }}
                    dependent={{ accessor: "siteId", key: "site.id", name: "Site", value: values.siteId ? values.siteId : siteId, childAcceccor: "accessTimePatternId" }}
                    selectedItemLabel={values.accessTimePatterns} />
            </div>
            <Label
                htmlFor="suspended"
                className="text-xs text-gray-600 mt-1 my-1">{translate.byKey(TranslationKeys.suspendedGroup)}:</Label>
            <div className="my-0">
                <Togglebox name="suspended" label="" />
            </div>
        </div>
    </div></>;
};
